import { Suspense, useState, type ReactNode } from "react";
import { BarChart3, Table2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { BotaoExportar } from "@/components/shared/BotaoExportar";

export type Coluna<T> = { titulo: string; valor: (l: T) => string | number | null; numero?: boolean; texto?: (l: T) => ReactNode };

type Props<T> = {
  titulo: string;
  descricao?: string;
  colunas: Coluna<T>[];
  linhas: T[];
  nomeDoArquivo: string;
  vazio?: string;
  className?: string;
  children: ReactNode;
};

const botao = (ativo: boolean) =>
  cn(
    "flex h-7 w-7 items-center justify-center rounded-md transition-colors",
    ativo ? "bg-white text-zinc-900 shadow-sm dark:bg-card dark:text-foreground" : "text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground",
  );

/** Cartão de gráfico com a opção de ver os mesmos números em tabela (e exportar). */
export function GraficoOuTabela<T>({ titulo, descricao, colunas, linhas, nomeDoArquivo, vazio = "Sem dados neste mês.", className, children }: Props<T>) {
  const [modo, setModo] = useState<"grafico" | "tabela">("grafico");
  return (
    <section className={cn("flex min-w-0 flex-col rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card", className)}>
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{titulo}</h3>
          {descricao && <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{descricao}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-lg bg-zinc-100 p-0.5 dark:bg-muted" role="group" aria-label={`Ver ${titulo} como`}>
          <button type="button" aria-label="Ver como gráfico" aria-pressed={modo === "grafico"} onClick={() => setModo("grafico")} className={botao(modo === "grafico")}>
            <BarChart3 className="h-3.5 w-3.5" />
          </button>
          <button type="button" aria-label="Ver como tabela" aria-pressed={modo === "tabela"} onClick={() => setModo("tabela")} className={botao(modo === "tabela")}>
            <Table2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      {linhas.length === 0 ? (
        <p className="flex flex-1 items-center justify-center py-12 text-sm text-zinc-500 dark:text-muted-foreground">{vazio}</p>
      ) : modo === "grafico" ? (
        <div className="h-64 min-w-0">
          <Suspense fallback={<div className="h-full animate-pulse rounded-lg bg-zinc-100 dark:bg-muted" />}>{children}</Suspense>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="max-h-64 overflow-auto rounded-lg border border-zinc-100 dark:border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 dark:bg-muted dark:text-muted-foreground">
                <tr>
                  {colunas.map((c) => (
                    <th key={c.titulo} className={cn("px-3 py-2 font-semibold", c.numero ? "text-right" : "text-left")}>
                      {c.titulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, i) => (
                  <tr key={i} className="border-t border-zinc-100 dark:border-border/50">
                    {colunas.map((c) => (
                      <td key={c.titulo} className={cn("px-3 py-1.5 text-zinc-700 dark:text-foreground/80", c.numero && "text-right tabular-nums")}>
                        {c.texto ? c.texto(l) : c.valor(l)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <BotaoExportar nome={nomeDoArquivo} titulo={titulo.toLowerCase()} planilha="visao-financeira" quantidade={linhas.length} idDe={(l: T) => String(linhas.indexOf(l) + 1)} buscarLinhas={async () => linhas} colunas={colunas.map((c) => ({ titulo: c.titulo, valor: c.valor }))} />
          </div>
        </div>
      )}
    </section>
  );
}
